import React from "react";
import { useNotification } from "@refinedev/core";

const V = (import.meta as any).env;
const API_URL = V?.VITE_API_URL || V?.VITE_API_BASE_URL || "http://localhost:8085";

type TaskKind = "reindex";
type TaskStatus = string;

interface BaseTask {
  id: string;
  kind: TaskKind;
  status: TaskStatus;
  meta: Record<string, unknown>;
}

interface TaskTrackerContextValue {
  registerReindexJob: (jobId: string, meta?: Record<string, unknown>) => void;
}

const TaskTrackerContext = React.createContext<TaskTrackerContextValue | undefined>(undefined);

const TERMINAL_STATUSES = new Set(["SUCCEEDED", "FAILED"]);
const POLL_INTERVAL_MS = 5000;

export const TaskTrackerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { open } = useNotification();
  const taskMetaRef = React.useRef<Map<string, BaseTask>>(new Map());
  const pollersRef = React.useRef<Map<string, number>>(new Map());

  const clearPoller = React.useCallback((taskId: string) => {
    const timeoutId = pollersRef.current.get(taskId);
    if (timeoutId) {
      window.clearInterval(timeoutId);
      pollersRef.current.delete(taskId);
    }
  }, []);

  const finalizeTask = React.useCallback(
    (taskId: string, status: string, meta: Record<string, unknown>) => {
      clearPoller(taskId);
      const stored = taskMetaRef.current.get(taskId);
      taskMetaRef.current.delete(taskId);
      const mergedMeta: Record<string, unknown> = {
        kind: stored?.kind,
        ...(stored?.meta ?? {}),
        ...meta,
      };

      if (status === "SUCCEEDED") {
        const processed = Number(mergedMeta.processed ?? 0);
        const total = Number(mergedMeta.total ?? 0);
        const failed = Number(mergedMeta.failed ?? 0);

        if (failed > 0 && processed === 0) {
          open?.({
            type: "error",
            message: "Ошибка реиндексации",
            description: `Не удалось проиндексировать ни одного документа (${failed} ошибок)`,
          });
        } else if (failed > 0) {
          open?.({
            type: "error",
            message: "Реиндексация завершена с предупреждениями",
            description: `${processed} успешно, ${failed} ошибок из ${total}`,
          });
        } else {
          open?.({
            type: "success",
            message: "Реиндексация успешно завершена",
            description: `${processed} документов проиндексировано`,
          });
        }
      } else if (status === "FAILED") {
        const errorMessage = mergedMeta.error_message as string | undefined;
        open?.({
          type: "error",
          message: "Ошибка реиндексации",
          description: errorMessage || "Не удалось выполнить реиндексацию. Проверьте логи сервиса.",
        });
      }
    },
    [clearPoller, open]
  );

  React.useEffect(() => {
    const pollers = pollersRef.current;
    return () => {
      pollers.forEach((timeoutId) => window.clearInterval(timeoutId));
      pollers.clear();
    };
  }, []);

  const pollReindexJob = React.useCallback(
    (jobId: string) => {
      const taskId = `reindex:${jobId}`;
      const poll = async () => {
        try {
          const response = await fetch(`${API_URL}/api/v1/knowledge-base/reindex/${jobId}`);
          if (!response.ok) {
            throw new Error(await response.text());
          }
          const data = await response.json();
          const status = data.status;
          const previous = taskMetaRef.current.get(taskId);
          taskMetaRef.current.set(taskId, {
            id: taskId,
            kind: "reindex",
            status,
            meta: { ...(previous?.meta ?? {}), ...data, kind: "reindex" },
          });
          if (TERMINAL_STATUSES.has(status)) {
            finalizeTask(taskId, status, { ...data, kind: "reindex", error_message: data.error_message });
          }
        } catch (error) {
          finalizeTask(taskId, "FAILED", { kind: "reindex", error_message: String(error) });
        }
      };

      poll();
      const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
      pollersRef.current.set(taskId, intervalId);
    },
    [finalizeTask]
  );

  const registerReindexJob = React.useCallback(
    (jobId: string, meta?: Record<string, unknown>) => {
      const taskId = `reindex:${jobId}`;
      taskMetaRef.current.set(taskId, {
        id: taskId,
        kind: "reindex",
        status: "QUEUED",
        meta: { ...(meta ?? {}), kind: "reindex" },
      });
      pollReindexJob(jobId);
    },
    [pollReindexJob]
  );

  const value = React.useMemo(
    () => ({
      registerReindexJob,
    }),
    [registerReindexJob]
  );

  return <TaskTrackerContext.Provider value={value}>{children}</TaskTrackerContext.Provider>;
};

export const useTaskTracker = () => {
  const context = React.useContext(TaskTrackerContext);
  if (!context) {
    throw new Error("useTaskTracker must be used within TaskTrackerProvider");
  }
  return context;
};
